import { useState, useEffect, useCallback } from "react";
import {
  isPushSupported,
  getPermissionState,
  subscribeToPush,
  isSubscribedToPush,
} from "@/lib/push-notifications";

/**
 * Auto-prompt for push notifications.
 *
 * Shows a dismissible banner after the user has been using the app for a bit.
 * Only shows if:
 * - Push is supported in this browser
 * - Permission hasn't been denied
 * - User hasn't dismissed it before
 * - User isn't already subscribed
 */
export function PushNotificationPrompt() {
  const [show, setShow] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    // Don't show immediately — wait for user to settle in
    const timer = setTimeout(async () => {
      if (!isPushSupported()) return;
      if (getPermissionState() === "denied") return;
      // "Not now" only snoozes the question for a week
      const dismissedAt = Number(localStorage.getItem("pph-push-dismissed") || 0);
      if (dismissedAt && Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) return;

      const subscribed = await isSubscribedToPush();
      if (subscribed) return;

      setShow(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  const handleEnable = useCallback(async () => {
    setSubscribing(true);
    const success = await subscribeToPush();
    setSubscribing(false);
    if (success) {
      setShow(false);
    }
  }, []);

  const handleDismiss = useCallback(() => {
    localStorage.setItem("pph-push-dismissed", String(Date.now()));
    setShow(false);
  }, []);

  if (!show) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 rounded-lg border border-border bg-card p-4 shadow-lg sm:left-auto sm:right-4 sm:w-80">
      <p className="text-sm font-medium text-foreground">
        Enable notifications?
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        Get a notification with sound for new assignments, messages, notes and reminders, even when the app is closed.
      </p>
      <div className="mt-3 flex gap-2">
        <button
          onClick={handleEnable}
          disabled={subscribing}
          className="rounded-md bg-paris-red px-3 py-1.5 text-sm font-medium text-white hover:bg-paris-red-dark disabled:opacity-50"
        >
          {subscribing ? "Enabling..." : "Enable"}
        </button>
        <button
          onClick={handleDismiss}
          className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-muted-foreground hover:bg-muted"
        >
          Not now
        </button>
      </div>
    </div>
  );
}
